'use client'
import dynamic from 'next/dynamic'
import { Suspense, useEffect } from 'react'
import { useMonitorUser } from '@/features/authentication/utils/authentication/hooks/useMonitorUser'
import { OpenTheAppBannerContainer } from '@/features/shared/components/AppBanners/OpenTheAppBannerContainer'
import { OpenTheAppDialogContainer } from '@/features/shared/components/AppBanners/OpenTheAppDialogContainer'
import { useSyncExperimentsWithGlobalVariable } from '@/features/shared/state/Experiments/hooks'
import { useSyncFeatureFlagsWithGlobalVariable } from '@/features/shared/state/FeatureFlags/hooks'
import { NProgress } from '@/features/shared/utils/appStart/NProgress'
import { useRouteChangeTracking } from '@/features/shared/utils/appStart/useRouteChangeTracking'
import { useSyncServerSideExperimentsWithRum } from '@/features/shared/utils/appStart/useSyncServersideExpWithRum'
import { handleMarvAdviceCookieExpiration } from '@/features/shared/utils/marv'
import { useSetRegion } from '@/lib/datadog/useSetRegion'
import { useConsentChangeListener } from '@/lib/transcend/useConsentChangeListener'
import { SegwayDevTools } from '@/root/SegwayDevTools'
import { useCleanUrlOfRedirectParams } from '@/features/shared/utils/appStart/useCleanUrlOfRedirectParams'
import { initApp } from '@/index'
import { useHandleMarvCampaign } from '@/features/shared/utils/appStart/useHandleMarvCampaign'
import { useLogExcessiveQueryObservers } from '@/features/shared/utils/appStart/useLogExcessiveQueryObservers'
import { useScrollRestoration } from '@/features/shared/utils/appStart/useScrollRestoration'
import { useRemoveRakutenBanner } from '@/features/shared/utils/appStart/useRemoveRakutenBanner'
import { useDataRights } from '@/features/account/services/DataPrivacy/hooks'
import { useQueryOptOutRestrictions } from '@/features/account/services/DataPrivacy/queries'
import { clearUserStoreCookie } from '@/features/shop/services/ShoppingStore/utils'
import { useUser } from '@/features/account/services/User/hooks'
import { useSession } from '@/features/authentication/utils/authentication/hooks/useSession'
import { useHandleLoginCompletion } from '@/features/authentication/utils/authentication/hooks/useHandleLoginCompletion'
import { useIdentifyUser } from '@/features/authentication/utils/authentication/hooks/useIdentifyUser'
import { useGlobalSignupPromoRedirect } from '@/features/authentication/services/SignupPromotions/useGlobalSignupPromoRedirect'
import { ThirdPartyScripts } from '@/lib/ThirdPartyScripts'
import { useQueryClient } from '@tanstack/react-query'
import { clearGuestCookies } from '@/features/account/services/GuestUser/utils'
import { useCurrentRouteData } from '@/features/shared/hooks/routes/useCurrentRouteData'
import { useSetRouteViewName } from '@/features/shared/utils/appStart/useSetRouteViewName'
import { useRoutingFlow } from '@/features/shared/utils/appStart/useRoutingFlow'
import { PersonaAnnouncer } from '@/features/business/components/PersonaAnnouncer'
import { ClientOnly } from '@/features/shared/components/ClientOnly'

const Toast = dynamic(() => import('@/features/shared/components/Toast').then((m) => m.Toast), {
  ssr: false,
})
const AuthenticatedUserStatePreloader = dynamic(() =>
  import('@/features/shared/utils/appStart/AuthenticatedUserStatePreloader').then(
    (m) => m.AuthenticatedUserStatePreloader,
  ),
)

const RouteDataEffects = () => {
  const routeData = useCurrentRouteData()
  useRoutingFlow(routeData)
  useSetRouteViewName(routeData)

  return null
}

const RoutingSideEffects = () => {
  useRouteChangeTracking()
  useCleanUrlOfRedirectParams()
  useScrollRestoration()
  useGlobalSignupPromoRedirect()

  return null
}

const CookieRemovalSideEffect = () => {
  useEffect(() => {
    // this is invoked here to handle scenarios where a user's session is expired but the marv
    // cookie does not get cleared on SSR since we currently only handle session expirations
    // on tracking events which does not get called on SSR
    handleMarvAdviceCookieExpiration()
  }, [])

  const user = useUser()
  const queryClient = useQueryClient()

  useEffect(() => {
    // these below cookies are only used when an authenticated user with a default shopping address is not present.
    // therefore, they should be removed on the client once they have a default shopping address.
    if (user?.default_shopping_address_id) {
      clearUserStoreCookie(queryClient)
      clearGuestCookies(queryClient)
    }
  }, [user?.default_shopping_address_id, queryClient])

  return null
}

const SideEffects = () => {
  useHandleLoginCompletion()
  useIdentifyUser()

  // initApp contains client-only code, so we want to run this in an effect
  // This function is called on every page nav. It's important it only _runs_ once (ie per page refresh)!
  useEffect(() => {
    initApp()
  }, [])

  useDataRights()
  useHandleMarvCampaign()
  useLogExcessiveQueryObservers()
  useQueryOptOutRestrictions({ notifyOnChangeProps: [] })

  return null
}

const ThirdPartySideEffects = () => {
  useSetRegion()
  useMonitorUser()
  useSyncServerSideExperimentsWithRum()
  useConsentChangeListener()
  useRemoveRakutenBanner()
  return null
}

const SyncStateWithTracking = () => {
  useSyncFeatureFlagsWithGlobalVariable()
  useSyncExperimentsWithGlobalVariable()
  return null
}

export const SegwayApp = ({ children }: { children: React.ReactNode }) => {
  const { userId } = useSession()

  return (
    <>
      {!!userId && <AuthenticatedUserStatePreloader />}
      {/* Suspense is added due to usages of useSearchParams */}
      <Suspense>
        <NProgress />
        <RouteDataEffects />
        <RoutingSideEffects />
        <SideEffects />
      </Suspense>

      <ThirdPartyScripts />
      <CookieRemovalSideEffect />
      <ThirdPartySideEffects />
      <SyncStateWithTracking />

      <OpenTheAppBannerContainer />
      <OpenTheAppDialogContainer />
      {children}

      {/* in app router, the session is seeded by a layout inside {children}. on the server, react may render this
          before that async layout resolves (userId is 0), but on the client it is always set, causing an intermittent 
          hydration mismatch. wrapping this with a <ClientOnly> forces the component to only render on the client. */}
      <ClientOnly>{!!userId && <PersonaAnnouncer />}</ClientOnly>
      <Toast />

      <SegwayDevTools />
    </>
  )
}
